import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFilter, faChevronLeft, faChevronRight, faEdit, faTrash, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const Formations = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [formations, setFormations] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState('');
  const [selectedSurveyType, setSelectedSurveyType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFormations, setTotalFormations] = useState(0);
  const formationsPerPage = 10;

  // chef_terrain and resp_cati already see one nature only, so the CATI/F2F filter would be a
  // no-op for them; it is offered to the roles whose list spans both.
  const canFilterBySurveyType = ['chef_terrain_superieur', 'administrateur_general', 'administrateur_general_superieur', 'resp_controle']
    .includes(currentUser?.role);

  useEffect(() => {
    fetchFormations();
    fetchSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedSurvey, selectedSurveyType, searchTerm]);

  const fetchFormations = async () => {
    try {
      setLoading(true);
      
      // For charge_etude, fetch all formations without pagination to filter by created_by
      // For other roles, use pagination
      const isChargeEtude = currentUser?.role === 'charge_etude';
      
      const params = new URLSearchParams();
      if (!isChargeEtude) {
        params.append('page', currentPage);
        params.append('per_page', formationsPerPage);
      }
      
      if (selectedSurvey) {
        params.append('survey_id', selectedSurvey);
      }
      
      if (selectedSurveyType) {
        params.append('survey_type', selectedSurveyType);
      }
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await api.get(`/formations?${params.toString()}`);
      let formations = response.data.data?.data || response.data.data || [];
      
      // For charge_etude role, filter to only show formations created by the current user
      if (isChargeEtude && currentUser?.id) {
        formations = formations.filter(f => f.created_by === currentUser.id);
        
        // Apply search filter if needed (already filtered by API, but double-check)
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          formations = formations.filter(f => 
            (f.title && f.title.toLowerCase().includes(searchLower)) ||
            (f.description && f.description.toLowerCase().includes(searchLower))
          );
        }
        
        // Apply survey filter if needed
        if (selectedSurvey) {
          formations = formations.filter(f => f.survey_id === parseInt(selectedSurvey));
        }
        
        // Calculate pagination for filtered results
        const totalFiltered = formations.length;
        setTotalFormations(totalFiltered);
        setTotalPages(Math.ceil(totalFiltered / formationsPerPage));
        
        // Apply pagination to filtered results
        const startIndex = (currentPage - 1) * formationsPerPage;
        formations = formations.slice(startIndex, startIndex + formationsPerPage);
      } else {
        setTotalPages(response.data.data?.last_page || response.data.last_page || 1);
        setTotalFormations(response.data.data?.total || response.data.total || 0);
      }
      
      setFormations(formations);
    } catch (error) {
      console.error('Error fetching formations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveys = async () => {
    try {
      const response = await api.get('/formations/surveys/list?active_only=true');
      if (response.data.success) {
        setSurveys(response.data.data || []);
      } else {
        setSurveys([]);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setSurveys([]);
    }
  };

  const handleCreateFormation = () => {
    navigate('/formations/create');
  };

  const handleEdit = (id) => {
    navigate(`/formations/${id}/edit`);
  };

  const handleView = (id) => {
    navigate(`/formations/${id}`);
  };

  const handleAssign = (id) => {
    navigate(`/formations/${id}/assign`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) {
      return;
    }

    try {
      await api.delete(`/formations/${id}`);
      fetchFormations();
    } catch (error) {
      console.error('Error deleting formation:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100';
  };

  const getVisibilityColor = (visibility) => {
    return visibility === 'public' ? 'text-blue-600 bg-blue-100' : 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 p-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center sm:text-left">
          GESTION DES FORMATIONS & BRIEFS
        </h1>
        <button 
          onClick={handleCreateFormation}
          className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Nouvelle Formation / Brief
        </button>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-4 sm:p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
              <div className="flex items-center space-x-2 flex-shrink-0">
                <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                <span className="font-medium text-gray-700 text-sm sm:text-base">Filtres:</span>
              </div>
              
              {/* Survey Filter */}
              <select
                value={selectedSurvey}
                onChange={(e) => {
                  setSelectedSurvey(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full sm:w-auto text-sm sm:text-base"
              >
                <option value="">Tous les surveys</option>
                {surveys.map((survey) => (
                  <option key={survey.id} value={survey.id}>{survey.title}</option>
                ))}
              </select>

              {/* Survey Type Filter (CATI / F2F) */}
              {canFilterBySurveyType && (
                <select
                  value={selectedSurveyType}
                  onChange={(e) => {
                    setSelectedSurveyType(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full sm:w-auto text-sm sm:text-base"
                >
                  <option value="">Tous les types</option>
                  <option value="cati">CATI</option>
                  <option value="f2f">Face à Face</option>
                </select>
              )}

              {/* Search */}
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  placeholder="Rechercher par titre ou description..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm sm:text-base"
                />
              </div>
            </div>
          </div>

          {/* Formations Table */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {loading ? (
              <div className="p-6 sm:p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-sm sm:text-base text-gray-600">Chargement des formations...</p>
              </div>
            ) : formations.length === 0 ? (
              <div className="p-6 sm:p-8 text-center">
                <p className="text-gray-600">Aucune formation ou brief trouvé</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Titre
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Survey
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Audience
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Visibilité
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {formations.map((formation) => (
                        <tr
                          key={formation.id}
                          onClick={() => handleView(formation.id)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm font-medium text-gray-900 break-words">
                              {formation.title}
                            </div>
                            {formation.description && (
                              <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                                {formation.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm text-gray-900">
                              {formation.survey?.title || 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm text-gray-900 capitalize">
                              {formation.target_audience === 'all' ? 'Tous' : formation.target_audience}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getVisibilityColor(formation.visibility)}`}>
                              {formation.visibility === 'public' ? 'Public' : 'Privé'}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(formation.is_active)}`}>
                              {formation.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            {/* Row itself opens the formation; the actions must not follow it. */}
                            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleAssign(formation.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Assigner"
                              >
                                <FontAwesomeIcon icon={faUsers} />
                              </button>
                              <button
                                onClick={() => handleEdit(formation.id)}
                                className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                title="Modifier"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                onClick={() => handleDelete(formation.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-200">
                  {formations.map((formation) => (
                    <div
                      key={formation.id}
                      onClick={() => handleView(formation.id)}
                      className="p-4 sm:p-5 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="mb-3">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 break-words">{formation.title}</h3>
                        {formation.description && (
                          <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2 break-words">{formation.description}</p>
                        )}
                      </div>
                      
                      <div className="mb-3 space-y-2">
                        <div className="text-xs sm:text-sm text-gray-700">
                          <span className="font-medium">Survey:</span> {formation.survey?.title || 'N/A'}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-700">
                          <span className="font-medium">Audience:</span> <span className="capitalize">{formation.target_audience === 'all' ? 'Tous' : formation.target_audience}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getVisibilityColor(formation.visibility)}`}>
                            {formation.visibility === 'public' ? 'Public' : 'Privé'}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(formation.is_active)}`}>
                            {formation.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => handleAssign(formation.id)} 
                          className="flex-1 px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center text-xs sm:text-sm font-medium"
                        >
                          <FontAwesomeIcon icon={faUsers} className="mr-1.5" />
                          Assigner
                        </button>
                        <button 
                          onClick={() => handleEdit(formation.id)} 
                          className="flex-1 px-3 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors flex items-center justify-center text-xs sm:text-sm font-medium"
                        >
                          <FontAwesomeIcon icon={faEdit} className="mr-1.5" />
                          Modifier
                        </button>
                        <button 
                          onClick={() => handleDelete(formation.id)} 
                          className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center text-xs sm:text-sm font-medium"
                        >
                          <FontAwesomeIcon icon={faTrash} className="mr-1.5" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Affichage de {(currentPage - 1) * formationsPerPage + 1} à {Math.min(currentPage * formationsPerPage, totalFormations)} sur {totalFormations} formations
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {currentPage} sur {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Formations;

